import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChildren,
  QueryList,
  forwardRef,
  HostListener,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface SearchableSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

let idCounter = 0;

@Component({
  selector: 'app-searchable-select',
  imports: [CommonModule, FormsModule],
  templateUrl: './searchable-select.component.html',
  styleUrl: './searchable-select.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchableSelectComponent),
      multi: true,
    },
  ],
})
export class SearchableSelectComponent implements ControlValueAccessor {
  private static readonly OTHER_VALUE = '__other__';
  private static openInstance: SearchableSelectComponent | null = null;

  @Input() options: SearchableSelectOption[] = [];
  @Input() placeholder = 'Selecciona una opcion';
  @Input() noResultsLabel = 'Sin coincidencias';
  @Input() disabled = false;
  @Input() inputId = `searchable-${++idCounter}`;
  @Input() showOtherOption = false;
  @Input() otherOptionLabel = 'Otro';

  @Output() valueSelected = new EventEmitter<string>();
  @Output() otherSelected = new EventEmitter<void>();

  @ViewChildren('optionBtn') optionButtons!: QueryList<ElementRef<HTMLButtonElement>>;

  isOpen = false;
  searchTerm = '';
  isTyping = false;
  activeIndex = -1;

  private currentValue = '';
  private onChange = (v: string) => {};
  private onTouched = () => {};

  constructor(private el: ElementRef) {}

  get isDisabled() {
    return this.disabled;
  }

  get hasSelection() {
    return !!this.currentValue;
  }

  get optionsWithOther(): SearchableSelectOption[] {
    if (!this.showOtherOption) return this.options;

    return [
      {
        value: SearchableSelectComponent.OTHER_VALUE,
        label: this.otherOptionLabel,
      },
      ...this.options,
    ];
  }

  get filteredOptions(): SearchableSelectOption[] {
    const list = [...this.optionsWithOther];

    // seleccionado arriba
    if (this.currentValue) {
      list.sort((a, b) =>
        a.value === this.currentValue ? -1 : b.value === this.currentValue ? 1 : 0,
      );
    }

    if (!this.isTyping || !this.searchTerm.trim()) {
      return list;
    }

    const q = this.searchTerm.toLowerCase();

    return list.filter(
      (o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q),
    );
  }

  onFocus() {
    this.open();
  }

  onToggleClick() {
    this.isOpen ? this.close() : this.open();
  }

  clearSelection(event?: MouseEvent) {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.isDisabled || !this.currentValue) return;

    this.currentValue = '';
    this.searchTerm = '';
    this.isTyping = false;
    this.activeIndex = -1;

    this.onChange('');
    this.onTouched();
    this.valueSelected.emit('');
    this.close();
  }

  open() {
    if (
      SearchableSelectComponent.openInstance &&
      SearchableSelectComponent.openInstance !== this
    ) {
      SearchableSelectComponent.openInstance.close();
    }

    SearchableSelectComponent.openInstance = this;

    this.isOpen = true;
    this.isTyping = false;
    this.syncLabel();
    this.setActiveToSelected();
  }

  close() {
    this.isOpen = false;
    this.activeIndex = -1;
  }

  onInput(e: Event) {
    const input = e.target as HTMLInputElement;
    this.searchTerm = input.value;

    this.isTyping = true;
    this.currentValue = '';
    this.onChange('');

    this.activeIndex = 0;
  }

  onBlur() {
    setTimeout(() => this.close(), 150);
  }

  onKeyDown(event: KeyboardEvent) {
    if (!this.isOpen) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.move(1);
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.move(-1);
        break;

      case 'Enter':
        event.preventDefault();
        if (this.activeIndex >= 0) {
          this.selectOption(this.filteredOptions[this.activeIndex]);
        }
        break;

      case 'Escape':
        this.close();
        break;
    }
  }

  move(step: number) {
    const len = this.filteredOptions.length;
    if (!len) return;

    do {
      this.activeIndex = (this.activeIndex + step + len) % len;
    } while (this.filteredOptions[this.activeIndex]?.disabled);

    this.scrollToActive();
  }

  setActiveToSelected() {
    const index = this.filteredOptions.findIndex((o) => o.value === this.currentValue);
    this.activeIndex = index >= 0 ? index : 0;

    setTimeout(() => this.scrollToActive());
  }

  scrollToActive() {
    const el = this.optionButtons.get(this.activeIndex)?.nativeElement;
    el?.scrollIntoView({ block: 'nearest' });
  }

  selectOption(option: SearchableSelectOption) {
    if (option.disabled) return;

    if (option.value === SearchableSelectComponent.OTHER_VALUE) {
      this.close();
      this.otherSelected.emit();
      return;
    }

    this.currentValue = option.value;
    this.searchTerm = option.label;
    this.isTyping = false;

    this.onChange(option.value);
    this.onTouched();
    this.valueSelected.emit(option.value);

    this.close();
  }

  highlight(label: string): string {
    if (!this.isTyping || !this.searchTerm) return label;

    const q = this.searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${q})`, 'gi');

    return label.replace(regex, `<mark>$1</mark>`);
  }

  syncLabel() {
    const found = this.optionsWithOther.find((o) => o.value === this.currentValue);
    this.searchTerm = found?.label ?? '';
  }

  trackByOptionValue(_: number, o: SearchableSelectOption) {
    return o.value;
  }

  @HostListener('document:click', ['$event'])
  clickOutside(e: MouseEvent) {
    if (!this.el.nativeElement.contains(e.target)) {
      this.close();
    }
  }

  onOptionMouseDown(event: MouseEvent): void {
    event.preventDefault();
  }

  writeValue(value: string): void {
    this.currentValue = value || '';
    this.syncLabel();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
}
